<template>
  <div class="brand-color">
    <div class="container">
      <div class="block intro">
        <div class="step-back">
          <a href="#" @click="goToStep">
            <img :src="getIcon('triangle.svg')" alt="" />
            <span>{{ $t('BrandColor.step_back') }}</span>
          </a>
        </div>
        <h1 class="heading">{{ $t('BrandColor.easily_configure') }}</h1>
        <p
          v-if="settings.step_2_description"
          v-html="settings.step_2_description"
        ></p>
        <p v-else>
          {{ $t('BrandColor.start_selecting') }}
        </p>
      </div>

      <RoomSelector v-if="hasRooms" />

      <div class="filters mobile">
        <div class="tab-header">
          <div
            class="tab arrows"
            :class="{ active: tab === 0 }"
            @click="tab = 0"
          >
            {{ $t('BrandColor.filter_brand') }}

            <div class="active-count-badge" v-if="selectedBrandIds.length">
              {{ selectedBrandIds.length }}
            </div>
          </div>
          <div
            class="tab arrows"
            :class="{ active: tab === 1 }"
            @click="tab = 1"
          >
            {{ $t('BrandColor.filter_color') }}

            <div class="active-count-badge" v-if="selectedColorIds.length">
              {{ selectedColorIds.length }}
            </div>
          </div>
        </div>
        <div class="tab-content">
          <transition name="next" mode="out-in">
            <BrandSelection v-if="tab === 0" :brands="brands" />
            <ColorSelection v-else :colors="colors" />
          </transition>
        </div>
      </div>

      <div class="filters">
        <BrandSelection :brands="brands" />
        <ColorSelection :colors="colors" />
      </div>
    </div>

    <LineResults :lines="results" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import LineResults from '../lines/LineResults';
import BrandSelection from '../brands/BrandSelection';
import ColorSelection from '../colors/ColorSelection';
import RoomSelector from '@/components/rooms/RoomSelector.vue';

export default {
  name: 'StepBrandColorSelection',
  components: {
    RoomSelector,
    ColorSelection,
    BrandSelection,
    LineResults,
  },
  data() {
    return {
      brands: [],
      colors: [],
      lines: [],
      tab: 0,
    };
  },
  created() {
    this.getBrandColorTypes();
  },
  watch: {
    selectedColorIds() {
      this.processFilters();
    },
    selectedBrandIds() {
      this.processFilters();
    },
    storeLines: {
      immediate: true,
      deep: true,
      handler() {
        this.setData();
      },
    },
  },
  computed: {
    ...mapState('filters', ['selectedBrandIds', 'selectedColorIds']),
    ...mapState('data', ['settings']),
    ...mapState('brand-color-selection', {
      storeBrands: 'brands',
      storeColors: 'colors',
      storeLines: 'lines',
    }),
    ...mapState('step', ['currentStep']),
    ...mapGetters('discount', ['calculateTotalPriceWithDiscount']),
    ...mapGetters('build', ['hasRooms']),

    results() {
      return this.lines.filter((line) => line.is_available);
    },
  },
  methods: {
    ...mapActions('brand-color-selection', ['getBrandColorTypes']),
    ...mapMutations('step', ['setStep']),
    setData() {
      this.brands = this.storeBrands.map((brand) => ({ ...brand }));
      this.colors = this.storeColors.map((color) => ({ ...color }));
      this.lines = this.storeLines.map((line) => ({ ...line }));
      this.processFilters();
    },
    goToStep() {
      this.$store.commit('brand-color-selection/setAllRoomsSelected', false);

      this.setStep(this.currentStep - 1);
    },
    processFilters() {
      this.filterLines();
      this.calculateBrands();
      this.calculateColors();
      this.sortBrands();
      this.sortColors();
      this.sortLines();
    },
    filterLines() {
      for (const line of this.lines) {
        const applyBrandFilter =
          this.selectedBrandIds.length === 0 ||
          this.selectedBrandIds.includes(line.brand_id);
        const applyColorFilter =
          this.selectedColorIds.length === 0 ||
          this.selectedColorIds.includes(line.color_id);

        line.is_available = applyBrandFilter && applyColorFilter;
      }
    },
    resetBrands() {
      let brands = {};

      for (const brand of this.brands) {
        brands[brand.id] = {
          ...brand,
          price: 0,
          is_available: false,
        };
      }

      return brands;
    },
    calculateBrands() {
      let brands = this.resetBrands();

      for (const line of this.lines) {
        if (
          this.selectedColorIds.length &&
          !this.selectedColorIds.includes(line.color_id)
        ) {
          continue;
        }
        const brandPrice = brands[line.brand_id].price;

        brands[line.brand_id].is_available = true;

        if (brandPrice === 0) {
          brands[line.brand_id].price = this.calculateTotalPriceWithDiscount(
            line.product_price_data
          );
        } else {
          brands[line.brand_id].price =
            brandPrice >
            this.calculateTotalPriceWithDiscount(line.product_price_data)
              ? this.calculateTotalPriceWithDiscount(line.product_price_data)
              : brandPrice;
        }
      }

      this.brands = Object.values(brands);
    },
    resetColors() {
      let colors = {};

      for (const color of this.colors) {
        colors[color.id] = {
          ...color,
          is_available: false,
        };
      }

      return colors;
    },
    calculateColors() {
      let colors = this.resetColors();

      for (const line of this.lines) {
        if (this.selectedBrandIds.length === 0) {
          colors[line.color_id].is_available = true;
        }
        if (
          this.selectedBrandIds.length &&
          !this.selectedBrandIds.includes(line.brand_id)
        ) {
          continue;
        }

        colors[line.color_id].is_available = true;
      }

      this.colors = Object.values(colors);
    },
    sortBrands() {
      this.brands.sort((a, b) => {
        if (!a.is_available) {
          return 1;
        } else if (!b.is_available) {
          return -1;
        }

        return a.price - b.price;
      });
    },
    sortColors() {
      this.colors.sort((a, b) => {
        if (!a.is_available) {
          return 1;
        } else if (!b.is_available) {
          return -1;
        }

        return 0;
      });
    },
    sortLines() {
      this.lines.sort((a, b) => a.total_price - b.total_price);
    },
  },
};
</script>
